import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import { UserPermissionService } from '../../services/user-permission-service';
import { US } from '../../constants/country-codes';
import Global from '../../store/global-selectors';
import { useIsSellerFeatureEnabled } from '../use-is-seller-feature-enabled';
import { RefundAssist } from '../../constants/features';
import { REFUND_ASSIST_STATUS_IDS } from '../../components/shipping-grid/columns/void-type';
import { emptyObject } from 'ui-components/default-props/empty';

/**
 * Hook to determine if Refund Assist should be visible to the seller
 * @returns {boolean} True if feature should be visible
 */
export const useIsRefundAssistVisible = () => {
  const sellerHomeCountry = useSelector(Global.selectSellerHomeCountry);
  const isRefundAssistVisible = useIsSellerFeatureEnabled(RefundAssist);

  return sellerHomeCountry === US && isRefundAssistVisible;
};

/**
 * Hook to determine if Refund Assist seller settings page should be visible
 * @returns {boolean} True if seller settings page should be visible
 */
export const useIsRefundAssistSettingsPageVisible = () => {
  const userRoles = useSelector(Global.selectUseruserRoles);
  const hasAccountManagementPermission = UserPermissionService.userHasAccountManagementRole(userRoles);
  const isRefundAssistVisible = useIsRefundAssistVisible();

  return isRefundAssistVisible && hasAccountManagementPermission;
};

/**
 * Hook to determine if Refund Assist grid should be visible
 * @returns {boolean} True if Refund Assist grid should be visible
 */
export const useIsRefundAssistGridVisible = () => {
  const isRefundAssistVisible = useIsRefundAssistVisible();
  const userRoles = useSelector(Global.selectUseruserRoles);
  const hasSellerSettingsPermission = useIsRefundAssistSettingsPageVisible();
  const hasOrderOrShippingPermission =
    UserPermissionService.userHasOrderManagementRole(userRoles) || UserPermissionService.userHasShippingRole(userRoles);

  // Admin users with seller settings permission can always see the grid
  if (hasSellerSettingsPermission) {
    return true;
  }

  return isRefundAssistVisible && hasOrderOrShippingPermission;
};

/**
 * Hook to check if any current page fulfillments have Refund Assist void type
 * @param {string[]} fulfillmentIds - Array of fulfillment IDs to check
 * @returns {boolean} True if any fulfillments have Refund Assist void type
 */
export const useHasRefundAssistVoidedShipments = (fulfillmentIds) => {
  const fulfillments = useSelector((state) => {
    return fulfillmentIds.map((id) => Global.selectEntitiesFulfillmentById(state, { fulfillmentId: id }) ?? emptyObject);
  });

  return useMemo(() => {
    return fulfillments.some((fulfillment) => {
      const { shipmentRefundStatusTypeId } = fulfillment;
      return REFUND_ASSIST_STATUS_IDS.includes(shipmentRefundStatusTypeId);
    });
  }, [fulfillments]);
};
